import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getSiteContent } from '../lib/api';
import { getCmsUrlValue, getCmsValue } from '../lib/siteContent';
import { buildFaqSchema, buildPublicStructuredData, buildSectionFaq, PAGE_SEO, useJsonLd, useSEO } from '../lib/seo';
import { Shell } from './SmokehouseSite';
import './SmokehouseSite.css';

const SECTION_NUMBERS = [1, 2, 3, 4];

/**
 * Sökordslandningssida (t.ex. /pulled-pork). All text, länkar och FAQ ligger i
 * CMS-sektionen `section`; title/description kommer från PAGE_SEO[path].
 */
export default function SeoLandingPage({ section, path }) {
  useSEO({ ...PAGE_SEO[path], path });
  const [siteContent, setSiteContent] = useState([]);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const { data } = await getSiteContent();
        if (!ignore && Array.isArray(data)) setSiteContent(data);
      } catch (e) {
        // behåll CMS-defaults
      }
    })();
    return () => {
      ignore = true;
    };
  }, []);

  const cms = (field) => getCmsValue(siteContent, section, field);
  const cmsUrl = (field) => getCmsUrlValue(siteContent, section, field);

  const structuredData = useMemo(
    () => buildPublicStructuredData(siteContent, path),
    [siteContent, path]
  );
  useJsonLd('public-route-structured-data', structuredData);

  // FAQ-schemat byggs ur samma CMS-fält som texten på sidan.
  const faqEntries = useMemo(() => buildSectionFaq(siteContent, section), [siteContent, section]);
  const faqSchema = useMemo(() => buildFaqSchema(faqEntries), [faqEntries]);
  useJsonLd('landing-faq', faqSchema);

  const sections = SECTION_NUMBERS
    .map((nr) => ({
      nr,
      title: cms(`section_${nr}_title`),
      body: cms(`section_${nr}_body`),
      ctaLabel: cms(`section_${nr}_cta_label`),
      ctaUrl: cmsUrl(`section_${nr}_cta_url`),
    }))
    .filter(({ title, body }) => title && body);

  return (
    <Shell siteContent={siteContent}>
      <section className="smoke-page smoke-landing">
        <div className="smoke-wrap">
          <h1>{cms('title')}</h1>
          <p className="smoke-lead">{cms('lead')}</p>
          <div className="smoke-landing-sections">
            {sections.map(({ nr, title, body, ctaLabel, ctaUrl }) => (
              <article className="smoke-panel" key={nr}>
                <h2>{title}</h2>
                <p>{body}</p>
                {ctaLabel && ctaUrl && <Link className="smoke-btn smoke-btn-secondary" to={ctaUrl}>{ctaLabel}</Link>}
              </article>
            ))}
          </div>
        </div>
      </section>

      {faqEntries.length > 0 && (
        <section className="smoke-section smoke-faq" aria-labelledby="landing-faq-heading">
          <div className="smoke-wrap">
            <h2 id="landing-faq-heading">{cms('faq_title')}</h2>
            <div className="smoke-faq-list">
              {faqEntries.map(({ slot, question, answer }) => (
                <details className="smoke-faq-item" key={slot}>
                  <summary>{question}</summary>
                  <p>{answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}
    </Shell>
  );
}
